<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import { UserService, type User, type CharityOrganizationUpdateData, type CharityUpdateResponse } from '../../lib/api/user.service';
  import { CategoryService, type Category } from '../../lib/api/category.service';
  import { AuthService } from '../../lib/api/auth.service';
  import { userStore } from '../../stores/auth.store';
  import Button from '../Button.svelte';
  import { CHARITY_DGR_TAX_OPTIONS } from '../../lib/charity/dgr-tax-status';

  export let isOpen = false;
  export let user: User | null = null;

  const dispatch = createEventDispatcher();
  
  let loading = false;
  let loadingCharityData = false;
  let charityData: any = null;
  
  // Categories for charity selection
  let categories: Category[] = [];
  let loadingCategories = false;
  
  // Field-specific errors
  let fieldErrors: Record<string, string[]> = {};

  // Charity organization data
  let organizationData: CharityOrganizationUpdateData = {
    name: '',
    abn: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    head_office_location: '',
    charity_government_id: '',
    category_uuid: '',
    dgr_tax_status: '',
    description: '',
    verified_bsb_no: '',
    verified_acc_no: '',
    verified_acc_name: ''
  };

  // Initialize form data when modal opens
  $: if (isOpen && user) {
    // Use charity data from user object instead of API call
    initializeCharityData();
    loadCategories();
  }

  // Load categories when component mounts
  onMount(async () => {
    await loadCategories();
  });
  
  async function loadCategories() {
    loadingCategories = true;
    try {
      const result = await CategoryService.getCategories();
      if (result.success && result.categories) {
        categories = result.categories;
      }
    } catch (err) {
      // Error loading categories - will use empty array
    } finally {
      loadingCategories = false;
  }
  }

  function initializeCharityData() {
    loadingCharityData = true;
    try {
      // Get charity data from user.organization.data
      if (user?.organization?.type === 'charity' && user?.organization?.data) {
        charityData = user.organization.data;
        
        // Initialize organization data
        organizationData = {
          name: charityData?.name || '',
          abn: charityData?.abn || charityData?.charity_government_id || '',
          contact_name: charityData?.contact_name || '',
          contact_email: charityData?.contact_email || '',
          contact_phone: charityData?.contact_phone || '',
          website: charityData?.website || '',
          head_office_location: charityData?.head_office_location || '',
          charity_government_id: charityData?.charity_government_id || '',
          category_uuid: charityData?.category_uuid || '',
          dgr_tax_status: charityData?.dgr_tax_status || '',
          description: charityData?.description || '',
          verified_bsb_no: charityData?.verified_bsb_no || '',
          verified_acc_no: charityData?.verified_acc_no || '',
          verified_acc_name: charityData?.verified_acc_name || ''
        };
      } else {
        dispatch('error', { message: 'No charity associated with this user' });
      }
    } catch (error: any) {
      dispatch('error', { message: 'Failed to load charity information' });
    } finally {
      loadingCharityData = false;
    }
  }

  function getFieldError(fieldName: string): string | null {
    return fieldErrors[fieldName] ? fieldErrors[fieldName][0] : null;
  }

  // Validation functions (same as registration)
  function validateEmail(email: string): string | null {
    if (!email) return null; // Email is optional for charity contact
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return null;
  }
  
  function validatePhone(phone: string): string | null {
    if (!phone) return null; // Phone is optional for charity contact
    // Basic phone validation - can be customized for specific formats
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/;
    if (!phoneRegex.test(phone)) return 'Please enter a valid phone number';
    return null;
  }
  
  function validateABN(abn: string): string | null {
    if (!abn) return 'ABN is required';
    // Basic ABN validation - remove spaces and check length
    const cleanABN = abn.replace(/\s/g, '');
    if (cleanABN.length !== 11) return 'ABN must be 11 digits';
    if (!/^\d+$/.test(cleanABN)) return 'ABN must contain only numbers';
    return null;
  }
  
  function validateURL(url: string): string | null {
    if (!url) return null; // URL is optional
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return null;
    } catch {
      return 'Please enter a valid URL';
    }
  }
  
  function validateRequired(value: string, fieldName: string): string | null {
    if (!value || value.trim() === '') {
      return `${fieldName} is required`;
    }
    return null;
    }

  function validateBankDetailsOptional(): string | null {
    const bsb = (organizationData.verified_bsb_no || '').trim();
    const acc = (organizationData.verified_acc_no || '').trim();
    const name = (organizationData.verified_acc_name || '').trim();
    const any = bsb || acc || name;
    if (!any) return null;
    if (!bsb || !acc || !name) {
      return 'Please enter BSB, account number, and account name together, or leave all bank fields empty.';
    }
    const bsbNorm = bsb.replace(/\D/g, '');
    if (bsbNorm.length !== 6) {
      return 'BSB must be 6 digits (dashes optional).';
    }
    return null;
  }

  function validateForm(): boolean {
    const errors: Record<string, string[]> = {};
    
    // Validate required fields
    const nameError = validateRequired(organizationData.name || '', 'Charity name');
    if (nameError) errors.name = [nameError];
    
    const categoryError = validateRequired(organizationData.category_uuid || '', 'Category');
    if (categoryError) errors.category_uuid = [categoryError];
    
    const abnError = validateABN(organizationData.abn || '');
    if (abnError) errors.abn = [abnError];

    if (!organizationData.dgr_tax_status) {
      errors.dgr_tax_status = ['Charity tax status is required'];
    }
    
    // Optional field validations
    if (organizationData.contact_name && organizationData.contact_name.length > 100) {
      errors.contact_name = ['Contact name cannot exceed 100 characters'];
    }

    if (organizationData.contact_email) {
      const contactEmailError = validateEmail(organizationData.contact_email);
      if (contactEmailError) errors.contact_email = [contactEmailError];
    }
    
    if (organizationData.contact_phone) {
      const contactPhoneError = validatePhone(organizationData.contact_phone);
      if (contactPhoneError) errors.contact_phone = [contactPhoneError];
    }
    
    if (organizationData.website) {
      const websiteError = validateURL(organizationData.website);
      if (websiteError) errors.website = [websiteError];
    }

    const bankErr = validateBankDetailsOptional();
    if (bankErr) errors.verified_bsb_no = [bankErr];
    
    // Set field errors and show notification if validation fails
    if (Object.keys(errors).length > 0) {
      fieldErrors = errors;
      
      const errorMessages = Object.entries(errors)
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join('; ');
      
      dispatch('error', { message: `Please fix the following errors: ${errorMessages}` });
      return false;
    }
    
    return true;
  }

  async function handleSubmit() {
    // Clear previous errors
    fieldErrors = {};
    
    // Perform client-side validation
    if (!validateForm()) {
      return; // Stop if validation fails
    }

    loading = true;
    
    try {
      const cleanAbn = (organizationData.abn || '').replace(/\s/g, '');
      const payload = {
        ...organizationData,
        abn: cleanAbn,
        charity_government_id: cleanAbn,
      };
      const result: CharityUpdateResponse = await UserService.updateCharityOrganization(payload);
      
      if (result.success) {
        // Update user object with new charity data
        if (user && user.organization && user.organization.data && result.charity) {
          const updatedUser = {
            ...user,
            organization: {
              ...user.organization,
              data: {
                ...user.organization.data,
                ...result.charity
              }
            }
          };
          
          // Update localStorage and user store
          localStorage.setItem('user', JSON.stringify(updatedUser));
          userStore.set(updatedUser as User);
          
          // Update local charityData for immediate UI reflection
          charityData = result.charity;
          
          dispatch('success', { 
            message: result.message || 'Charity organization updated successfully!',
            charity: result.charity,
            user: updatedUser
          });
        } else {
          dispatch('success', { 
            message: result.message || 'Charity organization updated successfully!',
            charity: result.charity
          });
        }
        
        // Close modal
        closeModal();
      } else {
        // Handle UserService error response
        if (result.error?.response?.data?.data?.errors) {
          const errors = result.error.response.data.data.errors;
          fieldErrors = errors;
        }
        dispatch('error', { message: result.error.response.data.data.errors || 'Failed to update charity information' });
      }
    } catch (error: any) {
      dispatch('error', { message: 'Failed to update charity information' });
    } finally {
      loading = false;
    }
  }

  function closeModal() {
    dispatch('close');
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      closeModal();
    } else if (event.key === 'Enter' && !loading) {
      handleSubmit();
    }
  }

  // Check if form has changes
  $: hasChanges = isOpen && charityData && (
    organizationData.name !== (charityData.name || '') ||
    organizationData.abn !== (charityData.abn || '') ||
    organizationData.contact_name !== (charityData.contact_name || '') ||
    organizationData.contact_email !== (charityData.contact_email || '') ||
    organizationData.contact_phone !== (charityData.contact_phone || '') ||
    organizationData.website !== (charityData.website || '') ||
    organizationData.head_office_location !== (charityData.head_office_location || '') ||
    organizationData.category_uuid !== (charityData.category_uuid || '') ||
    organizationData.dgr_tax_status !== (charityData.dgr_tax_status || '') ||
    organizationData.description !== (charityData.description || '') ||
    (organizationData.verified_bsb_no || '') !== (charityData.verified_bsb_no || '') ||
    (organizationData.verified_acc_no || '') !== (charityData.verified_acc_no || '') ||
    (organizationData.verified_acc_name || '') !== (charityData.verified_acc_name || '')
  );
</script>

{#if isOpen}
  <div class="modal-overlay" role="presentation" on:click|self={closeModal} on:keydown={(e) => e.key === 'Escape' && closeModal()}>
    <div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="charity-edit-profile-title">
      <div class="modal-header">
        <h3 id="charity-edit-profile-title">Edit Charity Organization</h3>
        <button class="modal-close" on:click={closeModal}>×</button>
      </div>
      
      <div class="modal-body">
        {#if loadingCharityData}
          <div class="loading-state">
            <p>Loading charity information...</p>
          </div>
        {:else}
          <form on:submit|preventDefault={handleSubmit}>
            <!-- Charity Organization Information -->
            <div class="section">
              
              <div class="form-group">
                <label for="charity_name">Charity Name <span class="required">*</span></label>
                <input 
                  type="text"
                  id="charity_name" 
                  bind:value={organizationData.name} 
                  required 
                  disabled={loading}
                  placeholder="Enter charity organization name"
                  class:error={getFieldError('name')}
                />
                {#if getFieldError('name')}
                  <div class="field-error">{getFieldError('name')}</div>
                {/if}
              </div>
              
              <div class="form-group">
                <label for="category">Category <span class="required">*</span></label>
                <select 
                  id="category" 
                  bind:value={organizationData.category_uuid} 
                  required 
                  disabled={loading || loadingCategories}
                  class:error={getFieldError('category_uuid')}
                >
                  <option value="">Select category...</option>
                  {#each categories as category}
                    <option value={category.uuid}>{category.name}</option>
                  {/each}
                </select>
                {#if getFieldError('category_uuid')}
                  <div class="field-error">{getFieldError('category_uuid')}</div>
                {/if}
              </div>
              
              <div class="form-group">
                <label for="abn">ABN <span class="required">*</span></label>
                <input 
                  type="text"
                  id="abn" 
                  bind:value={organizationData.abn}
                  required 
                  disabled={loading}
                  placeholder="11-digit ABN"
                  class:error={getFieldError('abn')}
                />
                {#if getFieldError('abn')}
                  <div class="field-error">{getFieldError('abn')}</div>
                {/if}
              </div>
              
              <div class="form-group">
                <label for="dgr_tax_status">Charity tax status <span class="required">*</span></label>
                <select
                  id="dgr_tax_status"
                  bind:value={organizationData.dgr_tax_status}
                  required
                  disabled={loading}
                  class:error={getFieldError('dgr_tax_status')}
                >
                  <option value="">Select tax status...</option>
                  {#each CHARITY_DGR_TAX_OPTIONS as option}
                    <option value={option.value}>{option.label}</option>
                  {/each}
                </select>
                {#if getFieldError('dgr_tax_status')}
                  <div class="field-error">{getFieldError('dgr_tax_status')}</div>
                {/if}
              </div>

              <div class="form-group">
                <label for="contact_name">Contact Name</label>
                <input
                  type="text"
                  id="contact_name"
                  bind:value={organizationData.contact_name}
                  disabled={loading}
                  placeholder="Contact name (optional)"
                  class:error={getFieldError('contact_name')}
                />
                {#if getFieldError('contact_name')}
                  <div class="field-error">{getFieldError('contact_name')}</div>
                {/if}
              </div>
              
              <div class="form-group">
                <label for="contact_email">Contact Email</label>
                <input 
                  type="email"
                  id="contact_email" 
                  bind:value={organizationData.contact_email}
                  disabled={loading}
                  placeholder="Contact email (optional)"
                  class:error={getFieldError('contact_email')}
                />
                {#if getFieldError('contact_email')}
                  <div class="field-error">{getFieldError('contact_email')}</div>
                {/if}
              </div>
              
              <div class="form-group">
                <label for="contact_phone">Contact Phone</label>
                <input 
                  type="tel"
                  id="contact_phone" 
                  bind:value={organizationData.contact_phone}
                  disabled={loading}
                  placeholder="Contact phone (optional)"
                  class:error={getFieldError('contact_phone')}
                />
                {#if getFieldError('contact_phone')}
                  <div class="field-error">{getFieldError('contact_phone')}</div>
                {/if}
              </div>
              
              <div class="form-group">
                <label for="website">Website</label>
                <input 
                  type="url"
                  id="website" 
                  bind:value={organizationData.website}
                  disabled={loading}
                  placeholder="https://example.com (optional)"
                  class:error={getFieldError('website')}
                />
                {#if getFieldError('website')}
                  <div class="field-error">{getFieldError('website')}</div>
                {/if}
              </div>
              
              <div class="form-group">
                <label for="head_office_location">Head Office Location</label>
                <input 
                  type="text"
                  id="head_office_location" 
                  bind:value={organizationData.head_office_location}
                  disabled={loading}
                  placeholder="Head office location (optional)"
                  class:error={getFieldError('head_office_location')}
                />
                {#if getFieldError('head_office_location')}
                  <div class="field-error">{getFieldError('head_office_location')}</div>
                {/if}
              </div>

              <div class="form-group full-width-field">
                <label for="description">Description</label>
                <textarea
                  id="description"
                  bind:value={organizationData.description}
                  disabled={loading}
                  placeholder="Short description of your charity (optional)"
                  rows="4"
                  maxlength="1000"
                  class:error={getFieldError('description')}
                ></textarea>
                {#if getFieldError('description')}
                  <div class="field-error">{getFieldError('description')}</div>
                {/if}
              </div>

              <p class="bank-modal-disclaimer">
                Bank details: Leave2Give will phone to verify before payments. Status: <strong>{charityData?.bank_details_status === 'verified' ? 'Verified' : 'Unverified'}</strong>.
              </p>
              <div class="form-group">
                <label for="verified_bsb_no">BSB</label>
                <input
                  type="text"
                  id="verified_bsb_no"
                  bind:value={organizationData.verified_bsb_no}
                  disabled={loading}
                  placeholder="e.g. 062-000"
                  class:error={getFieldError('verified_bsb_no')}
                  autocomplete="off"
                />
                {#if getFieldError('verified_bsb_no')}
                  <div class="field-error">{getFieldError('verified_bsb_no')}</div>
                {/if}
              </div>
              <div class="form-group">
                <label for="verified_acc_no">Account number</label>
                <input
                  type="text"
                  id="verified_acc_no"
                  bind:value={organizationData.verified_acc_no}
                  disabled={loading}
                  placeholder="Account number"
                  class:error={getFieldError('verified_acc_no')}
                  autocomplete="off"
                />
                {#if getFieldError('verified_acc_no')}
                  <div class="field-error">{getFieldError('verified_acc_no')}</div>
                {/if}
              </div>
              <div class="form-group">
                <label for="verified_acc_name">Account name</label>
                <input
                  type="text"
                  id="verified_acc_name"
                  bind:value={organizationData.verified_acc_name}
                  disabled={loading}
                  placeholder="Name on the account"
                  class:error={getFieldError('verified_acc_name')}
                  autocomplete="off"
                />
                {#if getFieldError('verified_acc_name')}
                  <div class="field-error">{getFieldError('verified_acc_name')}</div>
                {/if}
              </div>
            </div>
          </form>
          
    
        {/if}
      </div>
      
      <div class="modal-footer">
        <Button 
          variant="secondary" 
          on:click={closeModal}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          on:click={handleSubmit}
          disabled={loading || loadingCharityData || !hasChanges}
        >
          {loading ? 'Updating...' : 'Update Organization'}
        </Button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
  }

  .modal-content {
    background-color: #fff;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    width: 90%;
    max-width: 550px;
    max-height: 90%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: modalFadeIn 0.3s ease-out;
  }

  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    background: linear-gradient(135deg, #ffb400 0%, #f0a800 100%);
    color: white;
  }

  .modal-header h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
  }

  .modal-close {
    background: none;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s ease;
  }

  .modal-close:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  .modal-body {
    padding: 2rem;
    overflow-y: auto;
    flex-grow: 1;
  }

  .form-group {
    margin-bottom: 1.5rem;
  }

  .section {
    margin-bottom: 2rem;
    padding-bottom: 2rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .bank-modal-disclaimer {
    font-size: 0.8125rem;
    color: #6b7280;
    line-height: 1.45;
    margin: 0 0 1rem 0;
    padding: 0.5rem 0.75rem;
    background: #fffbeb;
    border-radius: 6px;
    border: 1px solid #fed7aa;
  }

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #374151;
  }
  
  .required {
    color: #dc2626;
  }
  
  input, select {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    outline: none;
    font-family: inherit;
    box-sizing: border-box;
  }
  
  input.error, select.error {
    border-color: #dc2626;
  }
  
  input::placeholder {
    color: rgba(0, 0, 0, 0.25);
  }
  
  input:focus, select:focus {
    border-color: #dc2626;
  }
  
  input.error:focus, select.error:focus {
    border-color: #dc2626;
  }

  .field-error {
    color: #dc2626;
    font-size: 0.875rem;
    margin-top: 0.25rem;
  }

  .modal-footer {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.5rem;
    background-color: #f8fafc;
    border-top: 1px solid #e2e8f0;
  }

  .modal-footer :global(button) {
    flex: 1;
    max-width: calc(50% - 0.5rem);
  }

  .loading-state {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;
    color: #6b7280;
    font-size: 1rem;
  }

  @media (max-width: 640px) {
    .modal-content {
      width: 95%;
      max-height: 95%;
    }
    
    .modal-body,
    .modal-footer {
      padding: 1.5rem;
    }
  }
</style> 